import { config } from "../config/config";

// Define the standard API response structure
export interface ApiSuccessResponse<T> {
	success: true;
	data: T;
	count?: number;
	timestamp: string;
}

export interface ApiErrorResponse {
	success: false;
	error: {
		message: string;
		code: number;
		details?: unknown;
		timestamp: string;
	};
}

export type ApiResponse<T> = ApiSuccessResponse<T> | ApiErrorResponse;

// Global refresh function - will be set by the auth hook
let globalRefreshFn: (() => Promise<any>) | null = null;
let isRefreshingGlobally = false;

// Set the global refresh function
export const setGlobalRefreshFunction = (refreshFn: () => Promise<any>) => {
	globalRefreshFn = refreshFn;
};

//legacy attmept to prevent infinite refresh attempts
// export const resetRefreshAttempts = (): void => {
// 	localStorage.removeItem("refreshAttempts");
// };

// Add a function to explicitly handle token refresh
export const refreshAuthToken = async (): Promise<boolean> => {
	try {
		// Prevent multiple concurrent refresh attempts
		if (isRefreshingGlobally) {
			console.log("Global refresh already in progress, skipping duplicate attempt");
			return false;
		}

		isRefreshingGlobally = true;
		console.log("Explicitly refreshing auth token");

		const response = await fetch(`${config.VITE_API_BASE_URL}/auth/refresh`, {
			method: "POST",
			credentials: "include", // Important: include cookies
			headers: {
				"Content-Type": "application/json",
			},
			cache: "no-store",
		});

		if (!response.ok) {
			console.error(`Token refresh failed with status ${response.status}`);
			return false;
		}

		const data = await response.json();
		console.log("Token refresh successful with response:", data.success);

		// IMPORTANT ADDITION: Force an immediate auth check after successful refresh
		// This bypasses throttling for the critical post-refresh auth check
		await checkAuthAfterRefresh();

		return data.success || true; // Return success status or default to true
	} catch (error) {
		console.error("Error refreshing token:", error);
		return false;
	} finally {
		isRefreshingGlobally = false;
	}
};

// New function to check auth immediately after refresh
const checkAuthAfterRefresh = async (): Promise<void> => {
	try {
		console.log("Performing immediate auth check after refresh");
		const response = await fetch(`${config.VITE_API_BASE_URL}/auth/me`, {
			credentials: "include",
			cache: "no-store",
		});

		if (response.ok) {
			const data = await response.json();
			if (data.success && data.data.user) {
				// Update auth controller state directly
				const authController =
					(window as any).authController || (await import("./auth-controller")).authController;
				authController.setAuthenticated(true, data.data.user);
				console.log("Auth state updated after successful refresh");
			}
		}
	} catch (error) {
		console.error("Error checking auth after refresh:", error);
	}
};

// The core fetch function with credential handling
export const fetchWithCredentials = async <T>(
	path: string,
	options: RequestInit = {},
	errorMessage?: string,
	dataOnly: boolean = false
): Promise<T> => {
	// Set default headers
	const headers = {
		"Content-Type": "application/json",
		...options.headers,
	};

	const normalizeApiPath = (input: string): string => {
		// Some environments mistakenly provide "/v1" instead of "/api/v1".
		// Normalize defensively so requests always hit the backend routes.
		if (input === "/v1") return "/api/v1";
		if (input.startsWith("/v1/")) return `/api/v1/${input.slice(4)}`;
		return input;
	};

	const normalizedPath = normalizeApiPath(path);

	const fullUrl = (() => {
		// Absolute URL: use as-is.
		if (normalizedPath.startsWith("http")) return normalizedPath;

		// Avoid double-prefixing when call sites already pass an API URL that begins with the base.
		// Example: base "/api/v1" + path "/api/v1/auth/login" -> should remain "/api/v1/auth/login"
		if (normalizedPath.startsWith(config.VITE_API_BASE_URL)) return normalizedPath;

		return `${config.VITE_API_BASE_URL}${normalizedPath}`;
	})();
	console.log("Making request to:", fullUrl);

	try {
		// Make the request with credentials
		const response = await fetch(fullUrl, {
			...options,
			headers,
			credentials: "include", // Include cookies with request
		});

		// Log response status for debugging
		console.log(`Response status: ${response.status} ${response.statusText} for ${path}`);

		// Try to parse the response as JSON
		let data;
		try {
			const textResponse = await response.text();

			try {
				data = textResponse ? JSON.parse(textResponse) : {};
				//	console.log("Parsed response data:", data);
			} catch (parseError) {
				console.error("Error parsing JSON response:", parseError);
				data = { success: false, error: "Invalid JSON response" };
			}
		} catch (textError) {
			console.error("Error reading response text:", textError);
			data = { success: false, error: "Could not read response" };
		}

		// Handle 401 Unauthorized (token expired)
		if (response.status === 401) {
			console.error("Received 401 Unauthorized");

			// Special handling for login endpoint - shouldn't attempt refresh
			if (path.includes("/auth/login")) {
				console.log("Login endpoint returned 401 - passing through error");

				// For login, return the error response directly
				if (!response.ok && data && data.error) {
					const error = new Error(data.error.message || "Authentication failed");
					// @ts-ignore
					error.response = data;
					// @ts-ignore
					error.status = response.status;
					throw error;
				}

				throw new Error("Authentication failed");
			}

			// If we have a refresh function and we're not already refreshing, try to refresh the token
			if (globalRefreshFn && !isRefreshingGlobally) {
				try {
					isRefreshingGlobally = true;
					await globalRefreshFn();
					isRefreshingGlobally = false;

					// If refresh was successful, retry the original request
					console.log("Retrying request after token refresh");
					return fetchWithCredentials<T>(path, options, errorMessage, dataOnly);
				} catch (refreshError) {
					// If refresh fails, create a detailed error
					isRefreshingGlobally = false;
					const error = new Error("Authentication failed");
					// @ts-ignore - Adding custom properties to Error
					error.response = data;
					// @ts-ignore
					error.status = response.status;
					console.error("Token refresh failed:", error);
					throw error;
				}
			} else {
				// Create a detailed error
				const error = new Error("Authentication failed");
				// @ts-ignore - Adding custom properties to Error
				error.response = data;
				// @ts-ignore
				error.status = response.status;
				console.error("Authentication failed without refresh attempt:", error);
				throw error;
			}
		}

		// Handle other error responses
		if (!response.ok) {
			// Extract error details from response
			if (data && data.error) {
				const message = data.error.message || errorMessage || "An error occurred";
				const error = new Error(message);
				// @ts-ignore
				error.response = data;
				// @ts-ignore
				error.status = response.status;
				throw error;
			}

			// Generic error if no specific error info found
			throw new Error(errorMessage || "An error occurred");
		}

		// Success - return data according to dataOnly flag
		if (dataOnly) {
			// If dataOnly is true, return just the data property
			return (data?.data as T) || (data as T);
		}

		// Otherwise return the full response
		return data as T;
	} catch (error) {
		console.error("Fetch error:", error);
		throw error;
	}
};

// Create the API client with methods for different HTTP verbs
export const apiClient = {
	get: async <T>(endpoint: string, errorMessage?: string, dataOnly: boolean = false): Promise<T> => {
		return fetchWithCredentials<T>(endpoint, { method: "GET" }, errorMessage, dataOnly);
	},

	post: async <T>(
		endpoint: string,
		data: any,
		errorMessage?: string,
		dataOnly: boolean = false
	): Promise<T> => {
		return fetchWithCredentials<T>(
			endpoint,
			{
				method: "POST",
				body: JSON.stringify(data),
			},
			errorMessage,
			dataOnly
		);
	},

	put: async <T>(
		endpoint: string,
		data: any,
		errorMessage?: string,
		dataOnly: boolean = false
	): Promise<T> => {
		return fetchWithCredentials<T>(
			endpoint,
			{
				method: "PUT",
				body: JSON.stringify(data),
			},
			errorMessage,
			dataOnly
		);
	},

	patch: async <T>(
		endpoint: string,
		data: any,
		errorMessage?: string,
		dataOnly: boolean = false
	): Promise<T> => {
		return fetchWithCredentials<T>(
			endpoint,
			{
				method: "PATCH",
				body: JSON.stringify(data),
			},
			errorMessage,
			dataOnly
		);
	},

	delete: async <T>(endpoint: string, errorMessage?: string, dataOnly: boolean = false): Promise<T> => {
		return fetchWithCredentials<T>(endpoint, { method: "DELETE" }, errorMessage, dataOnly);
	},

	deleteWithBody: async <T>(
		endpoint: string,
		data: any,
		errorMessage?: string,
		dataOnly: boolean = false
	): Promise<T> => {
		return fetchWithCredentials<T>(
			endpoint,
			{
				method: "DELETE",
				body: JSON.stringify(data),
			},
			errorMessage,
			dataOnly
		);
	},
};
